import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../Modal";
import CustomizedSnackbars from "../../customizedSnackbars";
import Spinner from "../Spinner";
import TextInput, { InputType } from "../../forms/textInput";
import PrimaryButton from "../PrimaryButton";
import classNames from "classnames";
import { COUNTRY } from "../../../pages/userStats/constant";
import styles from "./styles.module.scss";

// Import constants from the original cashout modal
import {
  ADDRESS,
  AMOUNT_MISMATCH_ERROR,
  BANK_ACCOUNT_NOT_ADDED,
  BANK_ACCOUNT_NUMBER,
  BANK_SINGLE_VIEW_ISSUE,
  BANK_TRANSFER_HEADER,
  BENEFICIARY_NAME,
  CHOOSE_REFUND_METHOD,
  CHOOSE_TRANSFER_METHOD,
  COMPLETE_CONVERSION,
  CONVERSION_IN_PROGRESS,
  MANUAL,
  MANUAL_BANK_TRANSFER,
  MOYASAR,
  MOYASAR_PLATFORM,
  MOYASAR_TRANSER_HEADER,
  OTHER_CONTRACT,
  OTHER_CONTRACT_PLATFORM,
  OTHER_CONTRACT_TRANSFER_HEADER,
  OTHER_REFUND_METHODS,
  RIYAL,
  SINGLE_VIEW,
  SINGLE_VIEW_PLATFORM,
  SINGLE_VIEW_TRANSER_HEADER,
  SUCCESS_MESSAGE,
  TABBY,
  TABBY_PLATFORM,
  TABBY_TRANSER_HEADER,
  NEO,
  NEO_PLATFORM,
  NEO_TRANSER_HEADER,
  TAMARA,
  TAMARA_PLATFORM,
  TAMARA_TRANSER_HEADER,
  TARGET_CONTRACT_ID,
  THE_AMOUNT_TO_BE_TRANSFERED,
  THE_BANK,
  TOTAL_DUE,
  TRANSACTION_DATE,
  TRANSFER_TO_REFERENCE_NUMBER,
  UNAVAILABLE,
  WAITING_FOR_CONVERSION,
} from "./constants";
import { checkUserBank } from "../../../setup/api/services/payment";
import { getUserDetailsById } from "../../../setup/api/services/user";
import {
  ESenderRole,
  ETrasactionsType,
} from "../../../setup/api/services/contract/models";
import { AlertColor } from "@mui/material";
import { IUserAddressResponse } from "../../../setup/api/services/user/models";
import { PaymentMethodModalProps, SelectedPaymentType } from "./types";
import DateTimePicker from "../DateTimePicker";
import { BankResponseData } from "../../../setup/api/services/payment/models";
import { useTranslation } from "react-i18next";

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  open,
  onClose,
  contract,
  userType,
  userId,
  amount,
  setAmount,
  gatewayPaymentId,
  setGatewayPaymentId,
  targetContractDomainId,
  setTargetContractDomainId,
  onConfirm,
  transactionDate,
  setTransactionDate,
  paymentAmount,
  bankDetails,
  showOtherContractOption = false,
  isTargetContractLocked = false,
  showBackToSourceOption = false,
}) => {
  const [showPending, setShowPending] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [selectionType, setSelectionType] = useState<SelectedPaymentType>(
    SelectedPaymentType.NONE
  );
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState<boolean>(false);

  const [userCurrentBankDetails, setUserCurrentBankDetails] = useState<
    BankResponseData | undefined
  >(undefined);
  const [address, setUserAddress] = useState<
    IUserAddressResponse["data"] | undefined
  >(undefined);

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");
  const [amountError, setAmountError] = useState<string>("");
  const [isManualRefund, setIsManualRefund] = useState<boolean>(false);
  // A back-to-source refund must never be retried from the UI: if it failed
  // after the destination contract was created, a second submit would create
  // another one. The backend logs the failure for reconciliation instead.
  const [hasAttemptedBackToSource, setHasAttemptedBackToSource] =
    useState<boolean>(false);

  const selectedBankDetails = useMemo(() => {
    return bankDetails ?? userCurrentBankDetails;
  }, [bankDetails, userCurrentBankDetails]);

  // Determine if the confirm button should be disabled based on selected payment method and required fields
  const isDisabled = useMemo(() => {
    // For other contract, require only targetContractDomainId
    if (selectionType === SelectedPaymentType.OTHER_CONTRACT) {
      return !targetContractDomainId;
    }
    // Back to source contract: nothing to fill in, and only one attempt
    if (selectionType === SelectedPaymentType.BACK_TO_SOURCE) {
      return hasAttemptedBackToSource;
    }
    // For bank transfer, require both bank details and gatewayPaymentId
    if (selectionType === SelectedPaymentType.BANK) {
      if (selectedBankDetails && gatewayPaymentId) return false;
      else return true;
    }
    // For manual refund (TABBY/MOYASAR/NEO with manual mode), require gatewayPaymentId
    if (
      isManualRefund &&
      (selectionType === SelectedPaymentType.TABBY ||
        selectionType === SelectedPaymentType.MOYASAR ||
        selectionType === SelectedPaymentType.NEO ||
        selectionType === SelectedPaymentType.TAMARA)
    ) {
      if (gatewayPaymentId) return false;
      else return true;
    }
    // For other payment methods (SingleView, Tabby, Moyasar, Neo automatic)
    if (
      selectionType === SelectedPaymentType.SINGLE_VIEW ||
      selectionType === SelectedPaymentType.TABBY ||
      selectionType === SelectedPaymentType.MOYASAR ||
      selectionType === SelectedPaymentType.NEO ||
      selectionType === SelectedPaymentType.TAMARA
    ) {
      // Require bank details and address for sellers, and amount for all users
      if (
        ((!selectedBankDetails || !address) &&
          userType !== ESenderRole.CUSTOMER) ||
        (!amount && !gatewayPaymentId)
      ) {
        return true;
      }
      // Prevent amount exceeding available cashout amount
      if (Number(amount) > paymentAmount) return true;
    }
    return false;
  }, [
    selectionType,
    selectedBankDetails,
    amount,
    gatewayPaymentId,
    targetContractDomainId,
    address,
    userType,
    paymentAmount,
    isManualRefund,
    hasAttemptedBackToSource,
  ]);

  const setSnackBarStates = useCallback((title: string, color: AlertColor) => {
    setSnackBarOpen(true);
    setSnackBarColor(color);
    setSnackBarTitle(title);
  }, []);

  /**
   * Handles the selection of a payment method
   * - Fetches user's bank details
   * - Fetches user's address information
   * - Updates the selection type
   */
  const handleSelection = useCallback(
    async (selection: SelectedPaymentType, manualRefund = false) => {
      setIsPending(true);
      setIsManualRefund(manualRefund);

      // Contract-to-contract transfers need no bank account or address
      if (
        selection === SelectedPaymentType.OTHER_CONTRACT ||
        selection === SelectedPaymentType.BACK_TO_SOURCE
      ) {
        setSelectionType(selection);
        setIsPending(false);
        return;
      }

      try {
        // Fetch user's bank account details if no bank details are provided
        if (!bankDetails && userId) {
          const userBankDetailsResponse = await checkUserBank(userId);
          setUserCurrentBankDetails(userBankDetailsResponse?.data ?? undefined);
        }
      } catch (error) {
        console.error(error);
        if (!manualRefund) {
          setIsManualRefund(false);
        }
      }
      try {
        // Fetch user's address details
        const result = await getUserDetailsById(Number(userId));
        setUserAddress(result?.data?.profile?.addresses[0]);
        setSelectionType(selection);
      } catch (error) {
        setSelectionType(selection);
        if (!manualRefund) {
          setIsManualRefund(false);
        }
        console.error(error);
      }
      setIsPending(false);
    },
    [setSelectionType, userId, bankDetails]
  );

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  const handleClose = useCallback(() => {
    setIsPending(false);
    setUserCurrentBankDetails(undefined);
    setUserAddress(undefined);
    setShowPending(false);
    setAmount(undefined);
    setGatewayPaymentId(undefined);
    setTargetContractDomainId(undefined);
    setTransactionDate(new Date());
    setAmountError("");
    setIsManualRefund(false);
    // hasAttemptedBackToSource is deliberately not reset: closing and reopening
    // the modal must not become a way to retry and create a second contract.
    onClose();

    setSelectionType(SelectedPaymentType.NONE);
  }, [
    onClose,
    setAmount,
    setGatewayPaymentId,
    setTargetContractDomainId,
    setTransactionDate,
  ]);

  const handleConfirm = useCallback(async () => {
    // Validation
    if (
      selectionType !== SelectedPaymentType.OTHER_CONTRACT &&
      (selectionType === SelectedPaymentType.SINGLE_VIEW ||
        (selectionType === SelectedPaymentType.MOYASAR && !isManualRefund) ||
        (selectionType === SelectedPaymentType.TABBY && !isManualRefund) ||
        (selectionType === SelectedPaymentType.NEO && !isManualRefund) ||
        (selectionType === SelectedPaymentType.TAMARA && !isManualRefund)) &&
      Number(amount) !== Number(paymentAmount)
    ) {
      setAmountError(AMOUNT_MISMATCH_ERROR);
      return;
    }

    // Execution
    setShowPending(true);
    if (selectionType === SelectedPaymentType.BACK_TO_SOURCE) {
      setHasAttemptedBackToSource(true);
    }

    setAmountError("");
    const { snackBarStates, shouldClose, success } = await onConfirm({
      selectionType,
      isManualRefund,
    });
    if (snackBarStates) {
      setSnackBarStates(snackBarStates.title, snackBarStates.color);
    }
    if (success) {
      setShowSuccess(true);
    }
    if (shouldClose) {
      handleClose();
    }

    setShowPending(false);
  }, [
    onConfirm,
    setSnackBarStates,
    handleClose,
    selectionType,
    amount,
    paymentAmount,
    isManualRefund,
  ]);

  const isSingleViewDisabled = useMemo(() => {
    // ignore if bankDetails is not passed from props to fetch current user's bank details
    if (!bankDetails) return false;

    return (
      !selectedBankDetails ||
      !selectedBankDetails?.bank?.swiftCode ||
      selectedBankDetails?.bank?.swiftCode === "" ||
      selectedBankDetails?.bank?.swiftCode?.toLowerCase() === "nan"
    );
  }, [bankDetails, selectedBankDetails]);

  /**
   * Renders the payment method selection view
   * - Shows different payment options based on user type (buyer/seller)
   * - Displays Tabby and Moyasar for buyers only
   * - Shows manual bank transfer and SingleView for all users
   */
  const renderSelection = useCallback(() => {
    return (
      <div className={styles["container"]}>
        {isPending ? (
          <Spinner />
        ) : (
          <div
            style={{ width: "100%" }}
            className={classNames({
              [styles["horizontal_section_container"]]:
                userType !== ESenderRole.CUSTOMER,
            })}>
            {userType === ESenderRole.CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "TABBY"
              ) && (
                <div className={styles["wide_section"]}>
                  <div className={styles["wide_section_content"]}>
                    <img
                      className={styles["avatar_img"]}
                      src="/assets/images/svg/tabby.svg"
                    />
                    <div className={styles["wide_section_content_text"]}>
                      <h2>{TABBY}</h2>
                      <p className={styles["p2"]}>{TABBY_PLATFORM}</p>
                    </div>
                  </div>
                  <div className={styles["buttons_row"]}>
                    <PrimaryButton
                      className={styles["button_auto"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.TABBY, false)
                      }>
                      <p>{t("lbl_automatic_transfer")}</p>
                    </PrimaryButton>
                    <PrimaryButton
                      className={styles["button_manual"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.TABBY, true)
                      }>
                      <p>{t("lbl_manual_transfer")}</p>
                    </PrimaryButton>
                  </div>
                </div>
              )}
            {userType === ESenderRole.CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "NEO"
              ) && (
                <div className={styles["wide_section"]}>
                  <div className={styles["wide_section_content"]}>
                    <img
                      className={styles["avatar_img"]}
                      src="/assets/images/svg/NEO.svg"
                    />
                    <div className={styles["wide_section_content_text"]}>
                      <h2>{NEO}</h2>
                      <p className={styles["p2"]}>{NEO_PLATFORM}</p>
                    </div>
                  </div>
                  <div className={styles["buttons_row"]}>
                    <PrimaryButton
                      className={styles["button_auto"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.NEO, false)
                      }>
                      <p>{t("lbl_automatic_transfer")}</p>
                    </PrimaryButton>
                    <PrimaryButton
                      className={styles["button_manual"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.NEO, true)
                      }>
                      <p>{t("lbl_manual_transfer")}</p>
                    </PrimaryButton>
                  </div>
                </div>
              )}
            {userType === ESenderRole.CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "TAMARA"
              ) && (
                <div className={styles["wide_section"]}>
                  <div className={styles["wide_section_content"]}>
                    <img
                      className={styles["avatar_img"]}
                      src="/assets/images/svg/Tamara.svg"
                    />
                    <div className={styles["wide_section_content_text"]}>
                      <h2>{TAMARA}</h2>
                      <p className={styles["p2"]}>{TAMARA_PLATFORM}</p>
                    </div>
                  </div>
                  <div className={styles["buttons_row"]}>
                    <PrimaryButton
                      className={styles["button_auto"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.TAMARA, false)
                      }>
                      <p>{t("lbl_automatic_transfer")}</p>
                    </PrimaryButton>
                    <PrimaryButton
                      className={styles["button_manual"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.TAMARA, true)
                      }>
                      <p>{t("lbl_manual_transfer")}</p>
                    </PrimaryButton>
                  </div>
                </div>
              )}
            {userType === ESenderRole.CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  (transaction?.paymentMethod === "CARD" ||
                    transaction?.paymentMethod === "APPLE_PAY" ||
                    transaction?.paymentMethod === "STCPAY")
              ) && (
                <div className={styles["wide_section"]}>
                  <div className={styles["wide_section_content"]}>
                    <img
                      style={{ height: "48px" }}
                      className={styles["avatar_img"]}
                      src="/assets/images/svg/moyasar.svg"
                    />
                    <div className={styles["wide_section_content_text"]}>
                      <h2>{MOYASAR}</h2>
                      <p className={styles["p2"]}>{MOYASAR_PLATFORM}</p>
                    </div>
                  </div>
                  <div className={styles["buttons_row"]}>
                    <PrimaryButton
                      className={styles["button_auto"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.MOYASAR, false)
                      }>
                      <p>{t("lbl_automatic_transfer")}</p>
                    </PrimaryButton>
                    <PrimaryButton
                      className={styles["button_manual"]}
                      onClick={() =>
                        handleSelection(SelectedPaymentType.MOYASAR, true)
                      }>
                      <p>{t("lbl_manual_transfer")}</p>
                    </PrimaryButton>
                  </div>
                </div>
              )}
            {userType === ESenderRole.CUSTOMER && (
              <h3 style={{ marginTop: "32px", marginBottom: "16px" }}>
                {OTHER_REFUND_METHODS}
              </h3>
            )}
            <div className={styles["payment_options"]}>
              <div
                onClick={() => handleSelection(SelectedPaymentType.BANK)}
                className={styles["section"]}>
                <img
                  className={styles["avatar_img"]}
                  src="/assets/images/svg/bank_transfer.svg"
                />
                <h4>{MANUAL}</h4>
                <p className={styles["p2"]}>{MANUAL_BANK_TRANSFER}</p>
              </div>
              {
                <div
                  onClick={() => {
                    if (isSingleViewDisabled) {
                      return;
                    }
                    handleSelection(SelectedPaymentType.SINGLE_VIEW);
                  }}
                  className={classNames(styles["section"], {
                    [styles["section_disabled"]]: isSingleViewDisabled,
                  })}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/single_view.svg"
                  />
                  <h4>{SINGLE_VIEW}</h4>
                  <p className={styles["p2"]}>{SINGLE_VIEW_PLATFORM}</p>
                  {isSingleViewDisabled && (
                    <p className={styles["error_text"]}>
                      {BANK_SINGLE_VIEW_ISSUE}
                    </p>
                  )}
                </div>
              }
              {showOtherContractOption && (
                <div
                  onClick={() =>
                    handleSelection(SelectedPaymentType.OTHER_CONTRACT)
                  }
                  className={styles["section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/contract_to_contract.svg"
                  />
                  <h4>{OTHER_CONTRACT}</h4>
                  <p className={styles["p2"]}>{OTHER_CONTRACT_PLATFORM}</p>
                </div>
              )}
              {showBackToSourceOption && (
                <div
                  onClick={() =>
                    handleSelection(SelectedPaymentType.BACK_TO_SOURCE)
                  }
                  className={styles["section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/contract_to_contract.svg"
                  />
                  <h4>{t("lbl_back_to_source_contract")}</h4>
                  <p className={styles["p2"]}>
                    {t("lbl_back_to_source_contract_description")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }, [
    isPending,
    userType,
    contract?.transactions,
    t,
    isSingleViewDisabled,
    handleSelection,
    showOtherContractOption,
    showBackToSourceOption,
  ]);

  /**
   * Renders the account details view after payment method selection
   * - Shows total due amount
   * - Displays bank account details (for bank transfer)
   * - Shows beneficiary information
   * - Displays address details
   * - Includes amount input field and confirm button
   */
  const renderAccountDetails = useCallback(() => {
    // Back to source contract: the backend creates the destination contract and
    // transfers the full due amount into it, so there is nothing to enter here.
    if (selectionType === SelectedPaymentType.BACK_TO_SOURCE) {
      return (
        <>
          <div className={styles["account_container"]}>
            <div className={styles["details"]}>
              <p>{TOTAL_DUE}</p>
              <p className={styles["detail_amount"]}>
                {paymentAmount.toFixed(2)}
                <span>{RIYAL}</span>
              </p>
            </div>
            <div className={styles["info_container"]}>
              <div className={styles["img_container"]}>
                <img src={"/assets/images/svg/exclamation.svg"} />
              </div>
              <p>{t("lbl_back_to_source_contract_hint")}</p>
            </div>
          </div>
          <div className={styles["button_container"]}>
            <PrimaryButton disabled={isDisabled} onClick={handleConfirm}>
              {COMPLETE_CONVERSION}
            </PrimaryButton>
          </div>
        </>
      );
    }

    if (selectionType === SelectedPaymentType.OTHER_CONTRACT) {
      return (
        <>
          <div className={styles["account_container"]}>
            <div className={styles["details"]}>
              <p>{TOTAL_DUE}</p>
              <p className={styles["detail_amount"]}>
                {paymentAmount.toFixed(2)}
                <span>{RIYAL}</span>
              </p>
            </div>
          </div>
          <div className={styles["text_area"]}>
            <TextInput
              handleChange={(event) =>
                setTargetContractDomainId(event.target.value)
              }
              type={InputType.DEFAULT}
              defaultValue={targetContractDomainId}
              placeholder={TARGET_CONTRACT_ID}
              disabled={isTargetContractLocked}
            />
          </div>
          <div className={styles["button_container"]}>
            <PrimaryButton disabled={isDisabled} onClick={handleConfirm}>
              {COMPLETE_CONVERSION}
            </PrimaryButton>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={styles["account_container"]}>
          {/* Show warning if bank details are missing for sellers */}
          {!selectedBankDetails && userType !== ESenderRole.CUSTOMER && (
            <div className={styles["info_container"]}>
              <div className={styles["img_container"]}>
                <img src={"/assets/images/svg/exclamation.svg"} />
              </div>
              <p>{BANK_ACCOUNT_NOT_ADDED}</p>
            </div>
          )}
          <div className={styles["warning_message"]}></div>
          <div className={styles["details"]}>
            <p>{TOTAL_DUE}</p>
            <p className={styles["detail_amount"]}>
              {paymentAmount.toFixed(2)}
              <span>{RIYAL}</span>
            </p>
          </div>
          {selectionType !== SelectedPaymentType.TABBY &&
            selectionType !== SelectedPaymentType.MOYASAR &&
            selectionType !== SelectedPaymentType.TAMARA && (
              <div className={styles["details"]}>
                <p>{BANK_ACCOUNT_NUMBER}</p>
                {selectedBankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {selectedBankDetails?.iban}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SelectedPaymentType.TABBY &&
            selectionType !== SelectedPaymentType.MOYASAR &&
            selectionType !== SelectedPaymentType.NEO &&
            selectionType !== SelectedPaymentType.TAMARA && (
              <div className={styles["details"]}>
                <p>{BENEFICIARY_NAME}</p>
                {selectedBankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {selectedBankDetails?.beneficiaryName}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SelectedPaymentType.TABBY &&
            selectionType !== SelectedPaymentType.MOYASAR &&
            selectionType !== SelectedPaymentType.NEO &&
            selectionType !== SelectedPaymentType.TAMARA && (
              <div className={styles["details"]}>
                <p>{THE_BANK}</p>
                {selectedBankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {selectedBankDetails?.bank?.arabicName}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SelectedPaymentType.TABBY &&
            selectionType !== SelectedPaymentType.MOYASAR &&
            selectionType !== SelectedPaymentType.NEO &&
            selectionType !== SelectedPaymentType.TAMARA && (
              <div className={styles["details"]}>
                <p>{ADDRESS}</p>
                {address ? (
                  <p className={styles["detail_desc"]}>
                    {COUNTRY[address?.countryCode]} - {address?.city} -
                    {address?.district} - {address?.street} -{" "}
                    {address?.buildingNo} -{address?.postalCode}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
        </div>
        {selectionType === SelectedPaymentType.BANK ||
        (isManualRefund &&
          (selectionType === SelectedPaymentType.TABBY ||
            selectionType === SelectedPaymentType.MOYASAR ||
            selectionType === SelectedPaymentType.NEO ||
            selectionType === SelectedPaymentType.TAMARA)) ? (
          <div className={styles["text_area"]}>
            <TextInput
              handleChange={(event) =>
                setGatewayPaymentId?.(event.target.value)
              }
              type={InputType.DEFAULT}
              defaultValue={gatewayPaymentId}
              placeholder={TRANSFER_TO_REFERENCE_NUMBER}
            />
          </div>
        ) : (
          <div className={styles["text_area"]}>
            <TextInput
              handleChange={(event) => {
                setAmount(event.target.value);
                setAmountError("");
              }}
              type={InputType.NUMBER}
              defaultValue={amount}
              placeholder={THE_AMOUNT_TO_BE_TRANSFERED}
              errorMessage={amountError ? amountError : undefined}
            />
          </div>
        )}
        {selectionType === SelectedPaymentType.BANK && (
          <div className={styles["text_area"]}>
            <DateTimePicker
              style={{ borderRadius: "0rem" }}
              value={transactionDate}
              placeholder={TRANSACTION_DATE}
              label={TRANSACTION_DATE}
              handleChange={(date) => {
                setTransactionDate(date);
              }}
            />
          </div>
        )}
        <div className={styles["button_container"]}>
          <PrimaryButton disabled={isDisabled} onClick={handleConfirm}>
            {COMPLETE_CONVERSION}
          </PrimaryButton>
        </div>
      </>
    );
  }, [
    selectedBankDetails,
    userType,
    paymentAmount,
    selectionType,
    address,
    amount,
    transactionDate,
    isDisabled,
    handleConfirm,
    setAmount,
    setTransactionDate,
    amountError,
    setGatewayPaymentId,
    gatewayPaymentId,
    isManualRefund,
    targetContractDomainId,
    setTargetContractDomainId,
    isTargetContractLocked,
    t,
  ]);

  // Auto-close the success modal after 5 seconds and refresh the page
  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
        window.location.reload();
      }, 5000);
    }
  }, [handleClose, showSuccess]);

  if (showPending) {
    return (
      <Modal size={"small"} visible={showPending} onCancel={handleClose}>
        <div className={styles["pending_container"]}>
          <img src="/assets/images/svg/pending.svg" />
          <h1>{CONVERSION_IN_PROGRESS}</h1>
          <p className={styles["p1"]}>{WAITING_FOR_CONVERSION}</p>
        </div>
      </Modal>
    );
  }

  if (showSuccess) {
    return (
      <Modal size={"small"} visible={showSuccess} onCancel={handleClose}>
        <div className={styles["success_container"]}>
          <img src="/assets/images/svg/Success_tick.svg" />
          <h1>{SUCCESS_MESSAGE}</h1>
        </div>
      </Modal>
    );
  }

  return (
    <Modal size={"medium"} visible={open} onCancel={handleClose}>
      {renderSnackBar()}
      {selectionType === SelectedPaymentType.NONE && (
        <h1>
          {userType === ESenderRole.CUSTOMER
            ? CHOOSE_REFUND_METHOD
            : CHOOSE_TRANSFER_METHOD}
        </h1>
      )}
      {selectionType === SelectedPaymentType.SINGLE_VIEW && (
        <h1>{SINGLE_VIEW_TRANSER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.TABBY && (
        <h1>{TABBY_TRANSER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.MOYASAR && (
        <h1>{MOYASAR_TRANSER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.NEO && (
        <h1>{NEO_TRANSER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.TAMARA && (
        <h1>{TAMARA_TRANSER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.BANK && (
        <h1>{BANK_TRANSFER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.OTHER_CONTRACT && (
        <h1>{OTHER_CONTRACT_TRANSFER_HEADER}</h1>
      )}
      {selectionType === SelectedPaymentType.BACK_TO_SOURCE && (
        <h1>{t("lbl_back_to_source_contract_header")}</h1>
      )}
      {selectionType === SelectedPaymentType.NONE && <>{renderSelection()}</>}
      {selectionType !== SelectedPaymentType.NONE && (
        <>{renderAccountDetails()}</>
      )}
    </Modal>
  );
};

export default PaymentMethodModal;
